import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Play } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import type { CompetitionRound } from '../../types/competition-round';

interface StartRoundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  round: CompetitionRound | null;
  /** Runs the start. `minutes` is undefined when the round has no duration. */
  onConfirm: (minutes: number | undefined) => Promise<void>;
}

/**
 * Says go, and says how long for.
 *
 * A round never starts by itself, and the length is a decision made in the room
 * — a session can be cut short or given ten more minutes on the day. So the
 * duration is asked for every time, prefilled with what the round was planned
 * at, and whatever is confirmed is what the round then runs for.
 */
export default function StartRoundDialog({
  open,
  onOpenChange,
  round,
  onConfirm,
}: StartRoundDialogProps) {
  const { t } = useTranslation();
  const [minutes, setMinutes] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMinutes(round?.duration_minutes ? String(round.duration_minutes) : '');
  }, [open, round]);

  const parsed = Number(minutes);
  // Blank is a real answer: a round with no duration runs until somebody closes
  // it by hand. Anything typed has to be a sane number of minutes.
  const blank = minutes.trim() === '';
  const valid = blank || (Number.isInteger(parsed) && parsed > 0 && parsed <= 24 * 60);

  const handleConfirm = async () => {
    if (!valid || starting) return;
    setStarting(true);
    try {
      await onConfirm(blank ? undefined : parsed);
      onOpenChange(false);
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('competitionRounds.startRound')}</DialogTitle>
          <DialogDescription>
            {t('competitionRounds.startDescription', { name: round?.name ?? '' })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="round-duration">{t('competitionRounds.durationMinutes')}</Label>
          <Input
            id="round-duration"
            type="number"
            inputMode="numeric"
            min={1}
            max={24 * 60}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
            autoFocus
          />
          <p className="text-xs text-muted-foreground">
            {t('competitionRounds.durationHelp')}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={starting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!valid || starting}>
            {starting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            {t('competitionRounds.startRound')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
